import React, { useEffect } from 'react';
import I18n from 'i18n-js';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import { formatBid, bumpRatePercentToDecimal, isValidBumpRatePercent, useBumpRateLimits } from './bump_helpers';
import { RCAlertBox, RCIcon, RCRadioCardGroup, RCTag } from '@reverbdotcom/cadence/components';
import { RCTextUtility } from '@reverbdotcom/cadence/components/RCText/RCText';
import { withPrefix } from '@reverbdotcom/commons/src/i18n_helpers';
import { LightningIcon, PencilSimpleIcon, TrophyIcon } from '@phosphor-icons/react';
import { FeaturedListingInsight } from '@reverbdotcom/commons/src/gql/graphql';

const RATE_VALUE_RECOMMENDED = 'recommended';
const RATE_VALUE_CUSTOM = 'custom';
export const SELL_THROUGH_PERCENT = '41';

const t = withPrefix('discovery.bumpModal');

export function RecommendedRadioV4({
  currentBid,
  recommendationAmount,
  handleBidChange,
  showCustomWarning,
  setShowCustomWarning,
  showTopPickVariant,
  insight,
  startAtMinimumRate,
}: {
  currentBid?: number;
  recommendationAmount: number;
  handleBidChange: (bid: number) => void;
  showCustomWarning: boolean;
  setShowCustomWarning: (show: boolean) => void;
  showTopPickVariant?: boolean;
  insight?: FeaturedListingInsight | null;
  startAtMinimumRate?: boolean;
}) {
  const limits = useBumpRateLimits();
  const currentPercent = currentBid ? formatBid(currentBid) : undefined;
  const startingPercent = startAtMinimumRate && currentPercent
    ? Math.max(currentPercent, limits.minimum)
    : currentPercent;

  const [selection, setSelection] = React.useState<string>(
    !!startingPercent && startingPercent !== recommendationAmount ? RATE_VALUE_CUSTOM : RATE_VALUE_RECOMMENDED,
  );

  const [customBid, setCustomBid] = React.useState<string>(
    String(startingPercent ?? recommendationAmount),
  );

  const isCurrentFeaturedWinner = insight?.featuredWinner;

  useEffect(() => {
    if (selection === RATE_VALUE_RECOMMENDED) {
      handleBidChange(bumpRatePercentToDecimal(recommendationAmount));
    }
  }, [recommendationAmount, selection, handleBidChange]);

  // the controller's selected bid starts at currentBid, so bump it up to match the input
  useEffect(() => {
    if (!!startingPercent && startingPercent !== currentPercent) {
      handleBidChange(bumpRatePercentToDecimal(startingPercent));
    }
  }, [startingPercent, currentPercent, handleBidChange]);


  function applyCustomBid(raw: string) {
    const value = Number.parseFloat(raw);
    // 0% is allowed here: it clears the bump
    const isValid = value === 0 || isValidBumpRatePercent(value, limits, startingPercent);
    setShowCustomWarning(!isValid);
    handleBidChange(Number.isFinite(value) ? bumpRatePercentToDecimal(value) : 0);
  }

  const handleCustomSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setCustomBid(next);
    applyCustomBid(next);
  };

  function handleRateChange(event: React.ChangeEvent<HTMLInputElement>) {
    const next = event.target.value;
    setSelection(next);

    if (next === RATE_VALUE_RECOMMENDED) {
      setShowCustomWarning(false);
      handleBidChange(bumpRatePercentToDecimal(recommendationAmount));
    } else {
      applyCustomBid(customBid);
    }
  }

  const recommendedIcon = showTopPickVariant ?
    <div className="fg-color-text-success">
      <RCIcon icon={<TrophyIcon />} size="5" />
    </div> :
    <div className="fg-color-text-accent">
      <RCIcon icon={<LightningIcon weight="duotone" />} size="5" />
    </div>;

  const recommendedSublabel = () => {
    if (showTopPickVariant && isCurrentFeaturedWinner) {
      return t('protectTopPickSublabel');
    }

    if (showTopPickVariant) {
      return t('topPickRecommendedSublabel');
    }

    return (
      <>
        <RCTextUtility size="350" weight="semibold" htmlTag="span">
          <Translate
            text="discovery.bumpModal.sellThroughCallout"
            args={{ percent: SELL_THROUGH_PERCENT }}
          />
        </RCTextUtility>{' '}
        <RCTextUtility size="350" color="secondary" htmlTag="span">
          <Translate text="discovery.bumpModal.sellThroughCalloutSubtitle" />
        </RCTextUtility>
      </>
    );
  };

  return (
    <div className="d-flex fx-dir-col">
      <div
        className="bump-modal-top-pick__rate-cards"
      >
        <RCRadioCardGroup
          id="bump-top-pick-rate"
          name="bump-top-pick-rate"
          value={selection}
          onChange={handleRateChange}
          label={t('setRateIteration')}
          visuallyHiddenLabel
          iconPlacement="hidden"
          iconAlignment="top"
        >
          <RCRadioCardGroup.Option
            value={RATE_VALUE_RECOMMENDED}
            label={
              <div className="d-flex fx-align-center fx-justify-between mobile-fx-dir-col mobile-fx-align-start mobile-gap-1">
                <div className="d-flex fx-align-center gap-2">
                  {recommendedIcon}
                  <RCTextUtility size="500" weight="bold">
                    {`${recommendationAmount}%`}
                  </RCTextUtility>
                </div>
                {!showTopPickVariant && <RCTag variant="highlight">
                  {t('recommended')}
                </RCTag>}
              </div>
            }
            sublabel={recommendedSublabel()}
          />
          <RCRadioCardGroup.Option
            value={RATE_VALUE_CUSTOM}
            label={
              <div className="d-flex fx-align-center gap-2">
                <RCIcon icon={<PencilSimpleIcon />} size="5" />
                <RCTextUtility size="500" weight="bold">
                  {t('topPickCustomLabel')}
                </RCTextUtility>
              </div>
            }
            sublabel={t('topPickCustomSublabel')}
          />
        </RCRadioCardGroup>
      </div>

      {selection === RATE_VALUE_CUSTOM && (
        <div className="d-flex fx-dir-col mt-3">
          <div className="width-100">
            <div className="input-group">
              <input
                type="number"
                aria-label={t('customRate')}
                className="input-group__input"
                value={customBid}
                onChange={handleCustomSelectChange}
              />
              <div className="input-group__text">%</div>
            </div>
          </div>
          {showCustomWarning && (
            <div className="mt-2">
              <RCAlertBox type="error" small>
                {I18n.t('discovery.bumpModal.customBumpWarning', {
                  minBump: limits.minimum,
                  maxBump: limits.maximum,
                  bumpIncrement: limits.increment,
                })}
              </RCAlertBox>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
